<!-- src/views/PlanWritePage.vue -->
<template>
  <ion-page>
    <ion-content :fullscreen="true" class="ion-padding">
      <b-slide-header />

      <div class="content-container">
        <div class="header-section">
          <h1 class="main-title">Min plan för: {{ goal?.label }}</h1>
          <p class="main-description">
            Beskriv kort vad du kommer att göra för att uppnå ditt mål.
          </p>
        </div>

        <div v-if="loading" class="loading-indicator">
          <ion-spinner></ion-spinner>
          <p>Laddar...</p>
        </div>
        <div v-else>
          <div class="factors-summary">
            <h2 class="summary-title">Valda faktorer att förbättra:</h2>
            <div v-if="selectedFactors.length > 0" class="selected-factors-list">
              <div v-for="factor in selectedFactors" :key="factor.category_id" class="factor-chip">
                {{ factor.label }}
              </div>
            </div>
            <p v-else>Inga faktorer valda.</p>
          </div>

          <div class="plan-input-section">
            <textarea
              v-model="planText"
              class="plan-textarea"
              placeholder="Skriv din plan här (max 300 tecken)..."
              maxlength="300"
            ></textarea>
            <div class="char-counter">{{ planText.length }} / 300</div>
          </div>
        </div>

        <div class="navigation-buttons">
          <b-button @click="back">Tillbaka</b-button>
          <b-button @click="next" :disabled="!isPlanValid">Fortsätt</b-button>
        </div>
      </div>
    </ion-content>
  </ion-page>
</template>

<script setup lang="ts">
import {
  IonContent,
  IonPage,
  IonSpinner,
  useIonRouter,
} from '@ionic/vue';
import BSlideHeader from '@/components/BSlideHeader.vue';
import BButton from '@/components/BButton.vue';
import { ref, onMounted, computed } from 'vue';
import { useStore } from '@/stores/main';
import { useRoute } from 'vue-router';

const store = useStore();
const router = useIonRouter();
const route = useRoute();
const loading = ref(true);
const goal = ref<any>(null);
const planText = ref('');
const selectedFactors = ref<any[]>([]);

const goalId = Number(route.params.goalId);

onMounted(async () => {
  try {
    const allGoals = await store.fetchGoals();
    goal.value = allGoals.find((g: any) => g.category_id === goalId);
    // Load existing plan text if available
    planText.value = goal.value?.action_plan || '';
    
    // fetch selected factors
    const allFactors = await store.fetchFactorsForGoal(goalId);
    selectedFactors.value = allFactors.filter((f: any) => f.selected);

  } catch (error) {
    console.error("Failed to load data:", error);
  } finally {
    loading.value = false;
  }
});

const isPlanValid = computed(() => {
  return planText.value.trim().length > 0;
});

function back() {
  router.back();
}

async function next() {
  // Save the plan text
  try {
    await store.updateGoalPlan(goalId, planText.value, selectedFactors.value);
    
    // Find the next goal or finish
    const allGoals = await store.fetchGoals();
    const improvingGoals = allGoals.filter((g: any) => g.selected === 1 && g.vision > g.value);
    const currentIndex = improvingGoals.findIndex((g: any) => g.category_id === goalId);

    if (currentIndex < improvingGoals.length - 1) {
      // Navigate to the next goal's action plan
      const nextGoalId = improvingGoals[currentIndex + 1].category_id;
      router.push(`/plan/${nextGoalId}/actions`);
    } else {
      // All goals are planned, navigate to the final pages
      router.push('/plan/results'); // Or feedback page first
    }
  } catch (error) {
    console.error("Failed to save plan:", error);
  }
}
</script>

<style scoped>
.content-container {
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
}

.header-section {
  margin-bottom: 2rem;
}

.main-title {
  font-size: 2rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 1rem;
}

.main-description {
  font-size: 1.1rem;
  color: #6b7280;
  line-height: 1.6;
}

.loading-indicator {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  color: #6b7280;
}

.factors-summary {
  margin-bottom: 2rem;
}

.summary-title {
  font-size: 1.2rem;
  font-weight: 600;
  margin-bottom: 1rem;
  color: #374151;
}

.selected-factors-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.factor-chip {
  background-color: #e3f2fd;
  color: #1976d2;
  padding: 0.5rem 1rem;
  border-radius: 16px;
  font-size: 0.9rem;
  font-weight: 500;
}

.plan-input-section {
  margin-top: 2rem;
}

.plan-textarea {
  width: 100%;
  height: 150px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  padding: 1rem;
  font-size: 1rem;
  resize: vertical;
  transition: border-color 0.3s ease;
}

.plan-textarea:focus {
  outline: none;
  border-color: #3b82f6;
}

.char-counter {
  text-align: right;
  color: #888;
  font-size: 0.9rem;
  margin-top: 0.5rem;
}

.navigation-buttons {
  display: flex;
  justify-content: space-between;
  margin-top: 2rem;
}

/* Mobile responsiveness */
@media (max-width: 768px) {
  .content-container {
    padding: 1rem;
  }
  
  .main-title {
    font-size: 1.5rem;
  }
  
  .main-description {
    font-size: 1rem;
  }
  
  .plan-textarea {
    height: 120px;
  }
}
</style>
